import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { API } from '../../../../../../../../api';
import { useAppSelector } from '../../../../../../../../redux/hooks';
import { useProgramContext } from '../../../../../../../../hooks/useProgramContext';
import { getLLAuthHeaders } from '../../../../../../../../utils/llAuthHeaders';
import { useOrgAudienceOptions } from '../../../learning-lifecycle/useOrgMemberAttributes';
import { AudienceGroups, GroupRole, RequirementsView, Scope, requirementsPilot, viewFromServer } from './requirements';
import {
  audienceName,
  audienceParts,
  isBlank,
  loadGroups
} from '../../../../../capability-setup/create/audienceGroups';
import { appliedTo, setsFromApi, type CapabilitySet } from '../../../../../capability-setup/sharedSets';

// One store for the page: the designer header (the grid) and each lesson's
// icon (its summary) open the same host.
const listeners = new Set<() => void>();
let openScope: Scope | null = null;

const emit = () => listeners.forEach((l) => l());
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
};

export const openRequirements = (scope: Scope): void => {
  openScope = scope;
  emit();
};

export const closeRequirements = (): void => {
  openScope = null;
  emit();
};

export const useOpenRequirementsScope = (): Scope | null => useSyncExternalStore(subscribe, () => openScope);

/** Whether this org sees requirements (The Playground, development builds only). */
export const useRequirementsPilot = (): boolean =>
  useAppSelector((state) => requirementsPilot((state.orgDetails?.data as { authId?: string } | undefined)?.authId));

/** What a role holds: its own capabilities, and the shared ones (Development, Standards) applied to it. */
const capabilityNamesOf = (role: { id: string; capabilities?: unknown }, sets: CapabilitySet[]): string[] => [
  ...(Array.isArray(role.capabilities) ? role.capabilities : []).map((c: { name?: unknown }) => String(c.name)),
  ...appliedTo(role, sets).map((c) => c.name)
];

/**
 * The program's Audience Requirements as the backend has them (GET
 * programs/{id}/audienceRequirements), read each time the grid or a lesson's
 * summary opens (`open`): the groups may have been saved since, and the
 * lessons published. With the backend on only (flags.ts).
 */
export const useAudienceRequirements = (
  grpId: string,
  open: boolean
): { view: RequirementsView | null; loading: boolean; error: string | null } => {
  const [state, setState] = useState<{ view: RequirementsView | null; loading: boolean; error: string | null }>({
    view: null,
    loading: false,
    error: null
  });
  useEffect(() => {
    if (!open || !grpId) return undefined;
    let alive = true;
    setState({ view: null, loading: true, error: null });
    API.getAudienceRequirements({ id: grpId, ...getLLAuthHeaders() })
      .then(({ data }) => alive && setState({ view: viewFromServer(data), loading: false, error: null }))
      .catch(
        () =>
          alive &&
          setState({
            view: null,
            loading: false,
            error: 'The audiences could not be loaded. Close this and try again.'
          })
      );
    return () => {
      alive = false;
    };
  }, [grpId, open]);
  return state;
};

// ── DUMMY, while audienceRequirementsBackend() is off: groups made up here ──

const NO_GROUPS: AudienceGroups = { readAssess: [], readOnly: [] };

// DUMMY: a Read Only audience group as the audience step would make it, from
// the org's own lists (Cian's example: Finance & Design · Management · London).
const PREFERRED = ['Finance', 'Design', 'Management', 'London'];
const dummyReadOnlyParts = (
  catalog: Array<{ name: string; values: Array<{ value: string }> }>
): Array<{ label: string; values: string[] }> =>
  catalog.slice(0, 3).map((list) => {
    const names = list.values.map((v) => v.value).filter(Boolean);
    const liked = names.filter((n) => PREFERRED.includes(n));
    return { label: list.name, values: liked.length ? liked : names.slice(0, 1) };
  });

/**
 * DUMMY: the program's two audience groups, as its build flow saved them in
 * this browser, else an example: Read & Assess = the program's roles (else
 * the org's first three), Read Only = one made-up audience from the org's
 * lists. Reads every role's capabilities to compute the cells here. The
 * backend's grid replaces all of it (useAudienceRequirements).
 */
export const useAudienceGroups = (grpId: string, enabled: boolean): { groups: AudienceGroups; loading: boolean } => {
  const { orgId, isPlatformAdmin } = useProgramContext();
  const { catalog, labelById } = useOrgAudienceOptions();
  const programRoleIds = useAppSelector((s: any) => s.groupDetails?.data?.roleIds as string[] | undefined);
  const [roles, setRoles] = useState<GroupRole[] | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    const forOrg = isPlatformAdmin ? orgId : undefined;
    Promise.all([
      API.getCapabilitySetup({ ...getLLAuthHeaders(), orgId: forOrg }),
      // Without the sets, a role still holds its own capabilities.
      API.getCapabilitySets({ ...getLLAuthHeaders(), orgId: forOrg }).catch(() => null)
    ])
      .then(([setup, setsRes]: any[]) => {
        if (!alive) return;
        const data = setup?.data?.data ?? setup?.data ?? {};
        const sets = setsFromApi((setsRes?.data?.data ?? setsRes?.data)?.sets);
        setRoles(
          (Array.isArray(data.roles) ? data.roles : []).map(
            (r: { id: unknown; name: unknown; capabilities?: unknown }) => {
              const id = String(r.id);
              return { id, name: String(r.name), capabilityNames: capabilityNamesOf({ ...r, id }, sets) };
            }
          )
        );
      })
      .catch(() => alive && setRoles([]));
    return () => {
      alive = false;
    };
  }, [enabled, orgId, isPlatformAdmin]);

  const groups = useMemo<AudienceGroups>(() => {
    // Off the pilot there is nothing to read or make up.
    if (!enabled) return NO_GROUPS;
    const all = roles ?? [];
    const saved = grpId ? loadGroups(grpId) : null;
    if (saved) {
      const roleName = (id: string) => all.find((r) => r.id === id)?.name ?? 'Role';
      const valueName = (id: string) => labelById.get(id) ?? '';
      const order = catalog.map((l) => l._id);
      const audiences = (list: typeof saved.readAssess) =>
        list
          .filter((a) => !isBlank(a))
          .map((a) => ({
            id: a.id,
            name: audienceName(a, roleName, valueName, order),
            parts: audienceParts(a, roleName, valueName, catalog),
            roles: all.filter((r) => a.roleIds.includes(r.id))
          }));
      return { readAssess: audiences(saved.readAssess), readOnly: audiences(saved.readOnly) };
    }
    // DUMMY: one role on its own, and two roles together (whose lessons differ by role).
    const inProgram = all.filter((r) => programRoleIds?.map(String).includes(r.id));
    const [solo, ...pair] = (inProgram.length ? inProgram : all).slice(0, 3);
    const roPart = dummyReadOnlyParts(catalog);
    const rolesPart = (rs: GroupRole[]) => [{ label: 'Roles', values: rs.map((r) => r.name) }];
    return {
      readAssess: [
        ...(solo ? [{ id: 'example-1', name: `${solo.name} Team`, parts: rolesPart([solo]), roles: [solo] }] : []),
        ...(pair.length
          ? [{ id: 'example-2', name: 'Example Combined Group', parts: rolesPart(pair), roles: pair }]
          : [])
      ],
      readOnly: [
        {
          id: 'example-ro',
          name: 'Stay in the Loop',
          parts: roPart,
          roles: []
        }
      ]
    };
  }, [enabled, grpId, roles, programRoleIds, catalog, labelById]);

  return { groups, loading: enabled && roles === null };
};
